import React, { useState, useEffect } from 'react';
import { UserPlus, Check, X, Loader2 } from 'lucide-react';
import { sendFollowRequest, getFollowRequestStatus } from '../../lib/follow';

interface FollowButtonProps {
  userId: string;
  onRequestSent?: () => void;
}

export function FollowButton({ userId, onRequestSent }: FollowButtonProps) {
  const [status, setStatus] = useState<'none' | 'pending' | 'accepted' | 'rejected'>('none');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadStatus();
  }, [userId]);

  const loadStatus = async () => {
    try {
      const currentStatus = await getFollowRequestStatus(userId);
      setStatus(currentStatus as 'none' | 'pending' | 'accepted' | 'rejected');
    } catch (error) {
      console.error('Error loading follow status:', error);
      setStatus('none'); // Fallback to 'none' on error
    }
  };

  const handleFollow = async () => {
    setLoading(true);
    setError('');

    try {
      await sendFollowRequest(userId);
      setStatus('pending');
      onRequestSent?.();
    } catch (error) {
      console.error('Error sending follow request:', error);
      setError(error instanceof Error ? error.message : 'Ein Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  if (status === 'pending') {
    return (
      <button
        disabled
        className="px-4 py-2 bg-background-secondary text-text-secondary rounded-full font-medium flex items-center gap-2"
      >
        <Check size={18} />
        Freundschaftsanfrage gesendet
      </button>
    );
  }

  if (status === 'accepted') {
    return (
      <button
        disabled
        className="px-4 py-2 bg-accent-primary text-white rounded-full font-medium flex items-center gap-2"
      >
        <Check size={18} />
        Befreundet
      </button>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={handleFollow}
        disabled={loading}
        className="px-4 py-2 bg-accent-primary hover:bg-accent-hover text-white rounded-full font-medium flex items-center gap-2 disabled:opacity-50"
      >
        {loading ? (
          <Loader2 size={18} className="animate-spin" />
        ) : (
          <UserPlus size={18} />
        )}
        Freund hinzufügen
      </button>
      {error && (
        <div className="absolute top-full mt-2 w-full p-2 bg-red-500/10 border border-red-500/50 rounded text-red-500 text-sm">
          {error}
        </div>
      )}
    </div>
  );
}